import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Analytics as AnalyticsIcon,
  AutoAwesome as AutoAwesomeIcon,
  Sync as SyncIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import AppPerformanceService from "../services/AppPerformanceService";
import AppService from "../services/AppService";
import { App } from "../types/app";
import {
  AppPerformanceAIAssessment,
  AppPerformanceSnapshot,
  PerformanceVerdict,
} from "../types/appPerformance";
import { useAuth } from "../hooks/useAuth";

const PERIOD_OPTIONS = [7, 30, 90] as const;

const verdictColor: Record<
  PerformanceVerdict,
  "success" | "info" | "warning" | "error"
> = {
  strong: "success",
  healthy: "info",
  watch: "warning",
  critical: "error",
};

const formatMetric = (
  value: number | null,
  unit: "count" | "currency" | "percent",
  currency?: string,
): string => {
  if (value === null || !Number.isFinite(value)) return "N/A";
  if (unit === "currency") {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (unit === "percent") {
    return `${value.toFixed(2)}%`;
  }
  return new Intl.NumberFormat().format(Math.round(value));
};

const AppPerformance: React.FC = () => {
  const { user } = useAuth();
  const [periodDays, setPeriodDays] = useState<number>(30);
  const [appsCatalog, setAppsCatalog] = useState<App[]>([]);
  const [selectedAppIds, setSelectedAppIds] = useState<string[]>([]);
  const [loadingApps, setLoadingApps] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [apps, setApps] = useState<AppPerformanceSnapshot[]>([]);
  const [fetchedAt, setFetchedAt] = useState<string>("");
  const [assessments, setAssessments] = useState<
    Record<string, AppPerformanceAIAssessment>
  >({});
  const [assessingAppId, setAssessingAppId] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<boolean>(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  const selectedAppNames = useMemo(() => {
    const names = appsCatalog
      .filter((app) => selectedAppIds.includes(app.appId))
      .map((app) => app.name);
    if (!names.length) return "No apps selected";
    if (names.length <= 2) return names.join(", ");
    return `${names.slice(0, 2).join(", ")} +${names.length - 2}`;
  }, [appsCatalog, selectedAppIds]);

  const loadRegisteredApps = async () => {
    if (!user?.email) return;
    setLoadingApps(true);
    setError(null);
    try {
      const result = await AppService.getUserApps(user.email);
      setAppsCatalog(result);
      setSelectedAppIds((current) => {
        const validCurrent = current.filter((id) =>
          result.some((app) => app.appId === id),
        );
        return validCurrent;
      });
    } catch (loadError: any) {
      setError(loadError?.message || "Failed to load registered apps.");
    } finally {
      setLoadingApps(false);
    }
  };

  React.useEffect(() => {
    loadRegisteredApps();
  }, [user?.email]);

  const loadData = async () => {
    if (!selectedAppIds.length) {
      setError("Select at least one app before fetching performance data.");
      return;
    }
    setLoading(true);
    setError(null);
    setSyncMessage(null);
    try {
      const result = await AppPerformanceService.fetchCatalog(
        periodDays,
        selectedAppIds,
      );
      setApps(result.apps || []);
      setFetchedAt(result.fetchedAt || new Date().toISOString());
      setAssessments({});
    } catch (loadError: any) {
      setError(loadError?.message || "Failed to load app performance.");
    } finally {
      setLoading(false);
    }
  };

  const lastUpdated = useMemo(() => {
    if (!fetchedAt) return "-";
    try {
      return new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(new Date(fetchedAt));
    } catch {
      return fetchedAt;
    }
  }, [fetchedAt]);

  const runAssessment = async (snapshot: AppPerformanceSnapshot) => {
    setAssessingAppId(snapshot.appId);
    try {
      const result = await AppPerformanceService.evaluateWithAI(
        snapshot,
        periodDays,
      );
      setAssessments((prev) => ({ ...prev, [snapshot.appId]: result }));
    } catch (assessmentError: any) {
      setError(assessmentError?.message || "Failed to evaluate app.");
    } finally {
      setAssessingAppId(null);
    }
  };

  const runSignalSync = async () => {
    if (!selectedAppIds.length) {
      setError("Select at least one app before syncing GA4/BigQuery signals.");
      return;
    }
    setSyncing(true);
    setError(null);
    setSyncMessage(null);
    try {
      const result = await AppPerformanceService.syncSignals(
        periodDays,
        selectedAppIds,
      );
      setSyncMessage(
        result?.message ||
          `Synced ${result.syncedCount} platform signal(s), ${result.errorCount} error(s), ${result.skippedCount} skipped.`,
      );
      await loadData();
    } catch (syncError: any) {
      setError(syncError?.message || "Failed to sync GA4/BigQuery signals.");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <Box sx={{ width: 1, minWidth: 0, overflowX: "hidden" }}>
      <Paper
        sx={{
          p: { xs: 1.75, md: 2 },
          mb: 2,
          borderRadius: 2,
          border: "1px solid",
          borderColor: "divider",
          background:
            "linear-gradient(135deg, rgba(14,116,144,0.14) 0%, rgba(255,255,255,1) 56%, rgba(34,197,94,0.1) 100%)",
        }}
      >
        <Stack spacing={1.1}>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1 }}>
              App Performance
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
              Select specific apps, then fetch only those performance metrics.
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 0.75, display: "block" }}
            >
              Last updated: {lastUpdated}
            </Typography>
          </Box>

          <Stack direction={{ xs: "column", lg: "row" }} spacing={1}>
            <TextField
              select
              size="small"
              label="Apps"
              value={selectedAppIds}
              onChange={(event) => {
                const value = event.target.value;
                setSelectedAppIds(
                  Array.isArray(value)
                    ? value.map((item) => String(item))
                    : String(value)
                        .split(",")
                        .map((item) => item.trim())
                        .filter(Boolean),
                );
              }}
              SelectProps={{
                multiple: true,
                renderValue: () => selectedAppNames,
              }}
              sx={{ minWidth: { xs: "100%", lg: 360 } }}
              disabled={loadingApps}
            >
              {appsCatalog.map((app) => (
                <MenuItem key={app.appId} value={app.appId}>
                  <Checkbox checked={selectedAppIds.includes(app.appId)} size="small" />
                  <Typography variant="body2" noWrap>
                    {app.name}
                  </Typography>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              size="small"
              label="Period"
              value={periodDays}
              onChange={(event) => setPeriodDays(Number(event.target.value || 30))}
              sx={{ minWidth: { xs: "100%", sm: 120 } }}
            >
              {PERIOD_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>
                  {option} days
                </MenuItem>
              ))}
            </TextField>

            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadRegisteredApps}
              disabled={loadingApps || loading}
            >
              Reload Apps
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<RefreshIcon />}
              onClick={loadData}
              disabled={loading || syncing || !selectedAppIds.length}
            >
              Fetch Selected
            </Button>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={syncing ? <CircularProgress size={16} /> : <SyncIcon />}
              onClick={runSignalSync}
              disabled={syncing || loading || !selectedAppIds.length}
            >
              {syncing ? "Syncing..." : "Sync Selected Signals"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : null}
      {syncMessage ? (
        <Alert
          severity="success"
          sx={{ mb: 1.5 }}
          onClose={() => setSyncMessage(null)}
        >
          {syncMessage}
        </Alert>
      ) : null}

      <Alert severity="info" sx={{ mb: 1.5 }}>
        Performance data is fetched only for selected apps. iOS sales reports require vendor number configuration.
      </Alert>

      {loading ? (
        <Box sx={{ textAlign: "center", py: 5 }}>
          <CircularProgress />
        </Box>
      ) : !selectedAppIds.length ? (
        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 2, textAlign: "center" }}
        >
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 700 }}>
            Select one or more apps
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Choose apps from the selector above, then click "Fetch Selected".
          </Typography>
        </Paper>
      ) : apps.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{ p: 3, borderRadius: 2, textAlign: "center" }}
        >
          <Typography variant="subtitle1" sx={{ mb: 0.5, fontWeight: 700 }}>
            No performance data loaded
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Click "Fetch Selected" to load performance data for the chosen apps.
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={1.25}>
          {apps.map((snapshot) => {
            const assessment = assessments[snapshot.appId];
            const isAssessing = assessingAppId === snapshot.appId;

            return (
              <Grid item xs={12} sm={6} lg={4} key={snapshot.appId}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    boxShadow: "0 6px 14px rgba(15,23,42,0.06)",
                  }}
                >
                  <CardContent sx={{ p: 1.35, "&:last-child": { pb: 1.35 } }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                      sx={{ minWidth: 0, mb: 0.75 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }} noWrap>
                          {snapshot.appName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {snapshot.platforms
                            .map((item) => item.platform.toUpperCase())
                            .join(" + ")}
                        </Typography>
                      </Box>
                      <Chip
                        icon={<AnalyticsIcon />}
                        label={formatMetric(
                          snapshot.combined.downloads.value,
                          snapshot.combined.downloads.unit,
                          snapshot.combined.downloads.currency,
                        )}
                        size="small"
                        variant="outlined"
                        sx={{ maxWidth: "48%" }}
                      />
                    </Stack>

                    <Grid container spacing={0.75}>
                      <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 0.9 }}>
                          <Typography variant="caption" color="text.secondary">
                            Revenue
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                            {formatMetric(
                              snapshot.combined.revenue.value,
                              snapshot.combined.revenue.unit,
                              snapshot.combined.revenue.currency,
                            )}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 0.9 }}>
                          <Typography variant="caption" color="text.secondary">
                            D7 Retention
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                            {formatMetric(
                              snapshot.combined.retentionDay7.value,
                              snapshot.combined.retentionDay7.unit,
                            )}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 0.9 }}>
                          <Typography variant="caption" color="text.secondary">
                            Crash
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                            {formatMetric(
                              snapshot.combined.crashRate.value,
                              snapshot.combined.crashRate.unit,
                            )}
                          </Typography>
                        </Paper>
                      </Grid>
                      <Grid item xs={6}>
                        <Paper variant="outlined" sx={{ p: 0.9 }}>
                          <Typography variant="caption" color="text.secondary">
                            Coverage
                          </Typography>
                          <Typography variant="subtitle2" sx={{ fontWeight: 700 }} noWrap>
                            {snapshot.diagnostics.length === 0
                              ? "Complete"
                              : `${snapshot.diagnostics.length} warning${snapshot.diagnostics.length > 1 ? "s" : ""}`}
                          </Typography>
                        </Paper>
                      </Grid>
                    </Grid>

                    {snapshot.diagnostics.length > 0 ? (
                      <Box
                        sx={{
                          mt: 0.9,
                          p: 0.8,
                          border: "1px solid",
                          borderColor: "warning.light",
                          borderRadius: 1.5,
                          bgcolor: "warning.50",
                        }}
                      >
                        {snapshot.diagnostics.slice(0, 2).map((item) => (
                          <Typography
                            key={item}
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", overflowWrap: "anywhere", lineHeight: 1.25 }}
                          >
                            • {item}
                          </Typography>
                        ))}
                      </Box>
                    ) : null}

                    <Box sx={{ mt: 1 }}>
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={
                          isAssessing ? <CircularProgress size={16} /> : <AutoAwesomeIcon />
                        }
                        onClick={() => runAssessment(snapshot)}
                        disabled={isAssessing}
                      >
                        {isAssessing ? "Evaluating..." : "AI Performance Verdict"}
                      </Button>
                    </Box>

                    {assessment ? (
                      <Paper variant="outlined" sx={{ mt: 1, p: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.6 }}>
                          <Chip
                            size="small"
                            color={verdictColor[assessment.verdict]}
                            label={`${assessment.verdict.toUpperCase()} • ${assessment.score}/100`}
                          />
                        </Stack>
                        <Typography
                          variant="caption"
                          sx={{ mb: 0.5, display: "block", overflowWrap: "anywhere" }}
                        >
                          {assessment.summary}
                        </Typography>
                        <Typography variant="caption" sx={{ fontWeight: 700, display: "block" }}>
                          Key drivers
                        </Typography>
                        {assessment.keyDrivers.map((item) => (
                          <Typography
                            key={item}
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere", lineHeight: 1.2 }}
                          >
                            • {item}
                          </Typography>
                        ))}
                        <Typography
                          variant="caption"
                          sx={{ fontWeight: 700, display: "block", mt: 0.5 }}
                        >
                          Next actions
                        </Typography>
                        {assessment.nextActions.map((item) => (
                          <Typography
                            key={item}
                            variant="caption"
                            display="block"
                            color="text.secondary"
                            sx={{ overflowWrap: "anywhere", lineHeight: 1.2 }}
                          >
                            • {item}
                          </Typography>
                        ))}
                      </Paper>
                    ) : null}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}
    </Box>
  );
};

export default AppPerformance;